var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { serializer, transformError, resolveQueryFieldsTransformationPaths, } from '@wix/metro-runtime/velo';
import * as ambassadorWixPaymentsRefundsV1Refund from '@wix/ambassador-payments-refunds-v1-refund/http';
// @ts-ignore
import { wrapWithQueryBuilder } from '@wix/motion-edm-autogen-query-wrapper';
const _toVeloEntity = '$';
const _fromVeloEntity = '$';
export var Status;
(function (Status) {
    Status["UNKNOWN_STATUS"] = "UNKNOWN_STATUS";
    /**
     * Initial status for all refunds.
     * Provisional, refund should be in this status for less than a minute.
     */
    Status["STARTING"] = "STARTING";
    /** Status right after STARTED for asynchronous refunds. */
    Status["PENDING"] = "PENDING";
    /**
     * Refund was successful.
     * Can transition to REVERSED in corner cases.
     */
    Status["SUCCEEDED"] = "SUCCEEDED";
    /** Regular error, terminal status. */
    Status["FAILED"] = "FAILED";
    /**
     * Either refund reversal
     * or any other error that comes after success, terminal status.
     */
    Status["REVERSED"] = "REVERSED";
})(Status || (Status = {}));
export var Initiator;
(function (Initiator) {
    Initiator["UNKNOWN_INITIATOR"] = "UNKNOWN_INITIATOR";
    Initiator["WIX"] = "WIX";
    Initiator["API"] = "API";
    Initiator["PROVIDER"] = "PROVIDER";
})(Initiator || (Initiator = {}));
export var SortOrder;
(function (SortOrder) {
    SortOrder["ASC"] = "ASC";
    SortOrder["DESC"] = "DESC";
})(SortOrder || (SortOrder = {}));
export var RejectionReason;
(function (RejectionReason) {
    RejectionReason["UNKNOWN_REJECTION_REASON"] = "UNKNOWN_REJECTION_REASON";
    /** Charge is already fully refunded. */
    RejectionReason["CHARGE_REFUNDED"] = "CHARGE_REFUNDED";
    /** Another refund was initiated for this charge and is waiting for confirmation from the provider. */
    RejectionReason["CHARGE_REFUND_IN_PROGRESS"] = "CHARGE_REFUND_IN_PROGRESS";
    /** Charge was disputed. */
    RejectionReason["CHARGE_DISPUTED"] = "CHARGE_DISPUTED";
    /** Charge is too old to be refunded. */
    RejectionReason["CHARGE_REFUND_PERIOD_ENDED"] = "CHARGE_REFUND_PERIOD_ENDED";
    /** Charge is unpaid. */
    RejectionReason["CHARGE_UNPAID"] = "CHARGE_UNPAID";
    /** PSP is temporarily down. */
    RejectionReason["PROVIDER_DOWN"] = "PROVIDER_DOWN";
    /**
     * Provider doesn't support refunds at the moment, transaction in a wrong state or we don't
     * have required information for this transaction.
     */
    RejectionReason["PROVIDER_NOT_SUPPORTED"] = "PROVIDER_NOT_SUPPORTED";
    /** Payment method of a charge doesn't support refunds. */
    RejectionReason["PAYMENT_METHOD_NOT_SUPPORTED"] = "PAYMENT_METHOD_NOT_SUPPORTED";
    /** Merchant account doesn't support refunds at the moment. */
    RejectionReason["MERCHANT_ACCOUNT_NOT_SUPPORTED"] = "MERCHANT_ACCOUNT_NOT_SUPPORTED";
    /** Merchant doesn't have enough balance to issue a refund for this charge. */
    RejectionReason["MERCHANT_BALANCE_INSUFFICIENT"] = "MERCHANT_BALANCE_INSUFFICIENT";
    /** Logged in merchant has no permission to refund this charge. */
    RejectionReason["NOT_AUTHORIZED"] = "NOT_AUTHORIZED";
})(RejectionReason || (RejectionReason = {}));
export var WebhookIdentityType;
(function (WebhookIdentityType) {
    WebhookIdentityType["UNKNOWN"] = "UNKNOWN";
    WebhookIdentityType["ANONYMOUS_VISITOR"] = "ANONYMOUS_VISITOR";
    WebhookIdentityType["MEMBER"] = "MEMBER";
    WebhookIdentityType["WIX_USER"] = "WIX_USER";
    WebhookIdentityType["APP"] = "APP";
})(WebhookIdentityType || (WebhookIdentityType = {}));
const _createRefundRequest = {};
const _createRefundResponse = {};
const _getRefundabilityRequest = {};
const _getRefundabilityResponse = {};
const _getRefundRequest = {};
const _getRefundResponse = {};
const _queryRefundsRequest = {};
const _queryRefundsResponse = {};
const _updateExtendedFieldsRequest = {};
const _updateExtendedFieldsResponse = {};
/**
 * Creates a refund.
 * Refunding process starts immediately after refund entity is created.
 *
 * If amount and currency are not specified,
 * refund is created for full charge amount.
 * If amount is specified, you also need to specify currency,
 * and it should be the same as charge currency.
 *
 * The call blocks until refund status transitions from `STARTING`.
 * Read more about refund statuses in this
 * [article](<https://dev.wix.com/docs/rest/business-management/payments/refunds/introduction#lifecycle-of-a-refund>).
 * @param refund - Refund to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField refund
 * @requiredField refund.chargeId
 * @adminMethod
 * @returns The created refund.
 */
export function createRefund(refund, options) {
    var _a, _b, _c;
    return __awaiter(this, arguments, void 0, function* () {
        const requestTransformation = {
            refund: '$[0]',
            returnProcessingFee: '$[1].returnProcessingFee',
            previouslyRefundedAmount: '$[1].previouslyRefundedAmount',
        };
        const responseTransformation = '$.refund';
        // @ts-ignore
        const { httpClient, sideEffects, additionalHeaders } = arguments[2];
        const { toAmbassadorRequest } = serializer({
            rootSchema: _createRefundRequest,
            depSchemas: {},
            fqdnTransformation: {
                paths: [...['refund']],
                transformation: _fromVeloEntity,
            },
            customTransformation: requestTransformation,
        });
        const { fromJSON } = serializer({
            rootSchema: _createRefundResponse,
            depSchemas: {},
            fqdnTransformation: {
                paths: [...['refund']],
                transformation: _toVeloEntity,
            },
            customTransformation: responseTransformation,
        });
        const payload = toAmbassadorRequest([refund, options]);
        const reqOpts = ambassadorWixPaymentsRefundsV1Refund.createRefund(payload);
        const reqOptionWithHeadersWrapper = (args) => {
            const result = reqOpts(args);
            if (additionalHeaders) {
                const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                return Object.assign(Object.assign({}, result), { headers });
            }
            return result;
        };
        (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
        try {
            const result = yield httpClient.request(reqOptionWithHeadersWrapper);
            (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
            return fromJSON(result.data);
        }
        catch (err) {
            const transformedError = transformError(err, requestTransformation, [
                'refund',
                'options',
            ]);
            (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
            throw transformedError;
        }
    });
}
/**
 * Retrieves a refund.
 * @param refundId - ID of the refund to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField refundId
 * @adminMethod
 * @returns The requested refund.
 */
export function getRefund(refundId) {
    var _a, _b, _c;
    return __awaiter(this, arguments, void 0, function* () {
        const requestTransformation = { refundId: '$[0]' };
        const responseTransformation = '$.refund';
        // @ts-ignore
        const { httpClient, sideEffects, additionalHeaders } = arguments[1];
        const { toAmbassadorRequest } = serializer({
            rootSchema: _getRefundRequest,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _fromVeloEntity,
            },
            customTransformation: requestTransformation,
        });
        const { fromJSON } = serializer({
            rootSchema: _getRefundResponse,
            depSchemas: {},
            fqdnTransformation: {
                paths: [...['refund']],
                transformation: _toVeloEntity,
            },
            customTransformation: responseTransformation,
        });
        const payload = toAmbassadorRequest([refundId]);
        const reqOpts = ambassadorWixPaymentsRefundsV1Refund.getRefund(payload);
        const reqOptionWithHeadersWrapper = (args) => {
            const result = reqOpts(args);
            if (additionalHeaders) {
                const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                return Object.assign(Object.assign({}, result), { headers });
            }
            return result;
        };
        (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
        try {
            const result = yield httpClient.request(reqOptionWithHeadersWrapper);
            (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
            return fromJSON(result.data);
        }
        catch (err) {
            const transformedError = transformError(err, requestTransformation, [
                'refundId',
            ]);
            (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
            throw transformedError;
        }
    });
}
/**
 * Retrieves a list of refunds, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 Refunds can be returned per request.
 *
 * To learn how to query refunds, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @public
 * @documentationMaturity preview
 * @permissionId PAYMENTS.REFUND_READ
 * @adminMethod
 */
export function queryRefunds() {
    const requestTransformation = { '*': '$[1]', query: '$[0]' };
    const responseTransformation = {
        items: '$.refunds',
        pagingMetadata: '$.pagingMetadata',
    };
    // @ts-ignore
    const { httpClient, sideEffects, additionalHeaders } = arguments[0];
    const { toAmbassadorRequest } = serializer({
        rootSchema: _queryRefundsRequest,
        depSchemas: {},
        fqdnTransformation: {
            paths: [],
            transformation: _fromVeloEntity,
        },
        customTransformation: requestTransformation,
    });
    const { fromJSON } = serializer({
        rootSchema: _queryRefundsResponse,
        depSchemas: {},
        fqdnTransformation: {
            paths: [...['Array#refunds']],
            transformation: _toVeloEntity,
        },
        customTransformation: responseTransformation,
    });
    return wrapWithQueryBuilder({
        func: (payload) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const reqOpts = ambassadorWixPaymentsRefundsV1Refund.queryRefunds(payload);
            const reqOptionWithHeadersWrapper = (args) => {
                const result = reqOpts(args);
                if (additionalHeaders) {
                    const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                    return Object.assign(Object.assign({}, result), { headers });
                }
                return result;
            };
            (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
            try {
                const result = yield httpClient.request(reqOptionWithHeadersWrapper);
                (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
                return result;
            }
            catch (err) {
                (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
                throw err;
            }
        }),
        requestTransformer: (...args) => toAmbassadorRequest(args),
        responseTransformer: ({ data }) => fromJSON(data),
        errorTransformer: (err) => {
            const transformedError = transformError(err, requestTransformation);
            throw transformedError;
        },
        pagingMethod: 'CURSOR',
        transformationPaths: resolveQueryFieldsTransformationPaths(_toVeloEntity),
    })({ cursorWithEmptyFilterAndSort: true });
}
/**
 * Updates extended fields of a refund without incrementing revision.
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @adminMethod
 */
export function updateExtendedFields(_id, namespace, options) {
    var _a, _b, _c;
    return __awaiter(this, arguments, void 0, function* () {
        const requestTransformation = {
            id: '$[0]',
            namespace: '$[1]',
            namespaceData: '$[2].namespaceData',
        };
        const responseTransformation = '$';
        // @ts-ignore
        const { httpClient, sideEffects, additionalHeaders } = arguments[3];
        const { toAmbassadorRequest } = serializer({
            rootSchema: _updateExtendedFieldsRequest,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _fromVeloEntity,
            },
            customTransformation: requestTransformation,
        });
        const { fromJSON } = serializer({
            rootSchema: _updateExtendedFieldsResponse,
            depSchemas: {},
            fqdnTransformation: {
                paths: [...['refund']],
                transformation: _toVeloEntity,
            },
            customTransformation: responseTransformation,
        });
        const payload = toAmbassadorRequest([_id, namespace, options]);
        const reqOpts = ambassadorWixPaymentsRefundsV1Refund.updateExtendedFields(payload);
        const reqOptionWithHeadersWrapper = (args) => {
            const result = reqOpts(args);
            if (additionalHeaders) {
                const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                return Object.assign(Object.assign({}, result), { headers });
            }
            return result;
        };
        (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
        try {
            const result = yield httpClient.request(reqOptionWithHeadersWrapper);
            (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
            return fromJSON(result.data);
        }
        catch (err) {
            const transformedError = transformError(err, requestTransformation, [
                '_id',
                'namespace',
                'options',
            ]);
            (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
            throw transformedError;
        }
    });
}
/**
 * Calculates refundability for a charge.
 *
 * Read more about refundability in this
 * [article](<https://dev.wix.com/docs/rest/business-management/payments/refunds/introduction#refundability>).
 * @param chargeId - ID of the charge for which refundability will be calculated.
 * @public
 * @documentationMaturity preview
 * @requiredField chargeId
 * @adminMethod
 */
export function getRefundability(chargeId) {
    var _a, _b, _c;
    return __awaiter(this, arguments, void 0, function* () {
        const requestTransformation = { chargeId: '$[0]' };
        const responseTransformation = '$';
        // @ts-ignore
        const { httpClient, sideEffects, additionalHeaders } = arguments[1];
        const { toAmbassadorRequest } = serializer({
            rootSchema: _getRefundabilityRequest,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _fromVeloEntity,
            },
            customTransformation: requestTransformation,
        });
        const { fromJSON } = serializer({
            rootSchema: _getRefundabilityResponse,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _toVeloEntity,
            },
            customTransformation: responseTransformation,
        });
        const payload = toAmbassadorRequest([chargeId]);
        const reqOpts = ambassadorWixPaymentsRefundsV1Refund.getRefundability(payload);
        const reqOptionWithHeadersWrapper = (args) => {
            const result = reqOpts(args);
            if (additionalHeaders) {
                const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                return Object.assign(Object.assign({}, result), { headers });
            }
            return result;
        };
        (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
        try {
            const result = yield httpClient.request(reqOptionWithHeadersWrapper);
            (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
            return fromJSON(result.data);
        }
        catch (err) {
            const transformedError = transformError(err, requestTransformation, [
                'chargeId',
            ]);
            (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
            throw transformedError;
        }
    });
}
//# sourceMappingURL=payments-refunds-v1-refund.universal.js.map